"use client";

import { Field, Group, Input, InputProps } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  guessCountryByPartialPhoneNumber,
  usePhoneInput,
} from "react-international-phone";

interface IProps {
  label?: string;
  onChange?: (phone: string) => void;
  value: string;
  disabled?: boolean;
}

type CombinedProps = InputProps & IProps;

const CPhoneInput: React.FC<CombinedProps> = ({
  label,
  onChange,
  value,
  disabled,
  ...props
}) => {
  const [phone, setPhone] = useState("");
  const [dialCode, setDialCode] = useState("");
  const [isDialFocus, setDialFocus] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const dialRef = useRef<HTMLInputElement | null>(null);
  const phoneRef = useRef<HTMLInputElement | null>(null);

  const phoneInput = usePhoneInput({
    disableDialCodeAndPrefix: true,
    defaultCountry: "uz",
    value: (dialCode || "") + (phone || "") || (props.readOnly ? value : ""),
  });

  // Initialize from value prop
  useEffect(() => {
    if (value && !isInitialized && !props.readOnly) {
      const country = guessCountryByPartialPhoneNumber({ phone: value });
      if (country.country?.dialCode) {
        const prefix = "+" + country.country.dialCode;
        setDialCode(prefix);
        setPhone(value.replace(prefix, ""));
      } else {
        setPhone(value);
      }
      setIsInitialized(true);
    }
  }, [value, isInitialized, props.readOnly]);

  // Call onChange callback
  useEffect(() => {
    if (onChange) {
      onChange(phoneInput.phone);
    }
  }, [phoneInput.phone]);

  // Update dial code when country changes
  useEffect(() => {
    if (!isDialFocus && phoneInput?.country?.dialCode) {
      const newDialCode = "+" + phoneInput.country.dialCode;
      if (dialCode !== newDialCode) {
        setDialCode(newDialCode);
      }
    }
  }, [phoneInput?.country?.dialCode]);

  // Memoize placeholder
  const placeholder = useMemo(
    () =>
      typeof phoneInput?.country?.format === "string"
        ? phoneInput.country.format?.replaceAll(".", "_")
        : "",
    [phoneInput?.country?.format]
  );

  // Handle dial code change
  const handleDialCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (newValue.length > 4) {
        setDialCode(newValue.substring(0, 4));
        phoneRef.current?.focus();
      } else {
        setDialCode(newValue);
      }
    },
    []
  );

  // Handle dial code blur
  const handleDialCodeBlur = useCallback(() => {
    setDialFocus(false);
    if (phoneInput?.country?.dialCode) {
      const correctDialCode = "+" + phoneInput.country.dialCode;
      if (dialCode !== correctDialCode) {
        setDialCode(correctDialCode);
      }
    }
  }, [dialCode, phoneInput?.country?.dialCode]);

  // Handle paste event
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedText = e.clipboardData.getData("text").trim();

      if (!pastedText) return;

      if (pastedText.startsWith("+")) {
        const newCountry = guessCountryByPartialPhoneNumber({
          phone: pastedText,
        });

        if (newCountry.country?.dialCode) {
          const prefix = "+" + newCountry.country.dialCode;
          const newPhone = pastedText.replace(prefix, "").replace(/\D/g, "");
          setDialCode(prefix);
          setPhone(newPhone);

          return;
        }
      }

      const cleanPhone = pastedText.replace(/\D/g, "");
      setPhone(cleanPhone);
    },
    []
  );

  const shouldDeleteExtraChar = (char: string | undefined) => {
    return char === " " || char === "-" || char === ")" || char === "(";
  };

  // Handle phone input change
  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputEvent = e.nativeEvent as InputEvent;

      if (!inputEvent.inputType) {
        setPhone(e.target.value);
        return;
      }

      if (inputEvent.inputType === "deleteContentBackward") {
        // If input is empty and user presses backspace, focus dial code
        if (shouldDeleteExtraChar(phoneInput.inputValue.at(-1))) {
          const secondLastChar = phoneInput.inputValue.at(-2);
          if (secondLastChar === ")") {
            setPhone(e.target.value.substring(0, e.target.value?.length - 2));
          } else {
            setPhone(e.target.value.substring(0, e.target.value?.length - 1));
          }
        } else {
          setPhone(e.target.value);
        }
      } else {
        setPhone(e.target.value);
      }
    },
    [phone, phoneInput.inputValue]
  );

  return (
    <Field.Root>
      <Field.Label htmlFor="phone" color="text.muted" fontSize="12px" mb={1}>
        {label}
      </Field.Label>
      <Group
        pt="6px"
        height="48px"
        bgColor="background"
        color={"text"}
        rounded="2xl"
        pb="6px"
        attached
        w="full"
        css={{
          "& > input": {
            pt: "6px",
            pb: "6px",
            lineHeight: 1.2,
            fontSize: "16px",
            outline: "none",
            height: "100%",
          },
        }}
      >
        <Input
          name="dial"
          type="tel"
          width="70px"
          ref={dialRef}
          borderWidth="0px"
          borderColor="#00000014"
          borderRightWidth="2px"
          value={dialCode}
          onChange={handleDialCodeChange}
          onFocus={() => setDialFocus(true)}
          onBlur={handleDialCodeBlur}
          aria-label="Country dial code"
          readOnly={props.readOnly}
          onKeyDown={() => {
            if (dialCode?.length === 4) {
              phoneRef?.current?.focus();
            }
          }}
          disabled={disabled}
        />
        <Input
          flex={1}
          type="tel"
          name="phone"
          border="none"
          ref={phoneRef}
          placeholder={placeholder}
          autoComplete="off"
          value={phoneInput.inputValue}
          onChange={handlePhoneChange}
          onPaste={handlePaste}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !phoneInput.inputValue) {
              dialRef?.current?.focus();
            }
          }}
          aria-label="Phone number"
          disabled={disabled}
          {...props}
        />
      </Group>
    </Field.Root>
  );
};

CPhoneInput.displayName = "PhoneInput";

export default CPhoneInput;
